import { Box, Link, Stack } from '@chakra-ui/react';
import { JSX, useMemo } from 'react';
import { formatDateTime, Icon } from 'shared-components';
import { IRecordedInterviewFulfilmentData, RecordedInterviewSystemStatus } from 'api-paths-cas';
import BaseFulfilmentDataRenderer, { FulfilmentRendererProps } from './BaseFulfilmentDataRenderer';
import { faPlay } from '@fortawesome/pro-regular-svg-icons';
import { Link as RouterLink, useParams } from 'react-router-dom';

export default function RecordedInterviewFulfilmentRenderer(props: FulfilmentRendererProps): JSX.Element | null {
  const { fulfilmentData, taskText, internalData } = props;
  const data = fulfilmentData as IRecordedInterviewFulfilmentData | null;
  const { uni, userId, applicationId, offerId } = useParams<{
    uni: string;
    userId: string;
    applicationId: string;
    offerId: string;
  }>();

  const videoId = data?.videoId;
  const recordingStatus = data?.recordingStatus;
  const attempts = data?.attempts;
  const hasVideo = !!videoId;
  const isExceeded = recordingStatus === RecordedInterviewSystemStatus.EXCEEDED;
  const interviewName = taskText ?? data?.welcomeTitle ?? 'Recorded interview';
  const taskId = data ? `RecordedInterview#${data.interviewId}` : '';
  const watchRoute = `/application/${uni}/${userId}/${applicationId}/offers/${offerId}/interviews/${encodeURIComponent(taskId)}/${encodeURIComponent(videoId ?? '')}`;

  const subtitle = useMemo(() => {
    const count = getAttemptsCount(attempts);
    const latestTimestamp = getLatestAttemptTimestamp(attempts);
    const attemptsLabel = count > 1 ? ` after ${count} attempts` : '';

    if (isExceeded && !hasVideo) {
      return `Recording not completed after ${count} attempts`;
    }
    if (hasVideo && latestTimestamp) {
      return `Recorded ${formatDateTime(new Date(latestTimestamp))}${attemptsLabel}`;
    }
    return '';
  }, [attempts, isExceeded, hasVideo]);

  const hasInternalFiles = (internalData?.urls?.length ?? 0) > 0;

  if (!data && !hasInternalFiles) return null;

  return (
    <Stack spacing={1}>
      {data && (
        <>
          <Box>
            {hasVideo ? (
              <Link as={RouterLink} to={watchRoute}>
                {interviewName} <Icon icon={faPlay} fontSize="xs" />
              </Link>
            ) : (
              <Box as="span">{interviewName}</Box>
            )}
          </Box>
          {subtitle && (
            <Box fontSize="sm" color="muted">
              {subtitle}
            </Box>
          )}
        </>
      )}
      <BaseFulfilmentDataRenderer {...props} />
    </Stack>
  );
}

function getLatestAttemptTimestamp(attempts: IRecordedInterviewFulfilmentData['attempts']): number | null {
  const entries = Object.values(attempts ?? {});
  if (entries.length === 0) return null;
  return entries.reduce((latest, entry) => (entry.timestamp > latest ? entry.timestamp : latest), 0);
}

function getAttemptsCount(attempts: IRecordedInterviewFulfilmentData['attempts']): number {
  return Object.keys(attempts ?? {}).length;
}
